/**
 * Use Cases & ROI page.
 *
 * Each card describes a concrete professional scenario, the workflow today
 * vs. with Ontoligent, and a quantified ROI estimate. Numbers are deliberately
 * conservative and based on industry-standard analyst benchmarks (McKinsey
 * "Knowledge worker productivity" 2023, Gartner "Cost of bad data" 2023,
 * IBM "Cost of a data breach" 2024) extrapolated to typical enterprise volumes.
 *
 * The intent is to give the reader a defensible, back-of-the-envelope answer
 * to: "why this and not generic RAG / a SaaS chatbot / homegrown LangChain?"
 */
import { useMemo, useState } from "react";
import {
  Banknote, Briefcase, ChevronRight, Clock, Database, FileText, FlaskConical,
  Gavel, KeyRound, Lightbulb, ListChecks, Lock, Network, Quote, Rocket,
  Search as SearchIcon, ShieldCheck, Stethoscope, TrendingUp, Workflow, X,
  type LucideIcon,
} from "lucide-react";
import { UseCaseGraph } from "@/components/UseCaseGraph";
import { ContactModal } from "@/components/ContactModal";
import KnowledgeGraphIllustration from "@/components/KnowledgeGraphIllustration";

/* Distinct, professional hues per industry — used to colour the knowledge-
   graph domain nodes and the legend. Falls back to the theme accent. */
const INDUSTRY_COLORS: Record<string, string> = {
  "Life sciences": "hsl(160 55% 45%)",
  "Insurance": "hsl(202 70% 52%)",
  "Banking": "hsl(43 78% 48%)",
  "Capital markets": "hsl(264 55% 62%)",
  "Healthcare / Pharma": "hsl(330 60% 58%)",
  "SecOps": "hsl(8 72% 58%)",
  "Legal": "hsl(220 16% 56%)",
};
function industryColor(industry: string): string {
  return INDUSTRY_COLORS[industry] ?? "hsl(var(--accent))";
}

/* ------------------------------------------------------------------ */
/* Canonical platform phases — authored once, reused by every domain.  */
/* The phases are identical across industries; only the per-domain     */
/* specifics change. That invariance is the wrapper thesis: an         */
/* enterprise drives Ontoligent over its own data on its own tenant.   */
/* ------------------------------------------------------------------ */

type BlueprintKey =
  | "ontology" | "sources" | "pipeline" | "retrieval" | "agents" | "governance";

interface PhaseDef {
  key: BlueprintKey;
  n: string;
  phase: string;
  surface: string;
  Icon: LucideIcon;
  /** Generic, security-forward description of what happens on this surface. */
  generic: string;
}

const PLATFORM_PHASES: PhaseDef[] = [
  {
    key: "ontology", n: "01", phase: "Model the domain", surface: "Ontology",
    Icon: Network,
    generic:
      "Author the entity and relation types as a versioned ontology. The published version becomes the extraction contract — agents only ever assert in-vocabulary facts. Only the schema is defined here; no source data is involved.",
  },
  {
    key: "sources", n: "02", phase: "Connect your sources", surface: "Catalog",
    Icon: Database,
    generic:
      "Register your systems of record as connectors. Credentials are encrypted per workspace with a tenant-scoped key, payloads are read under your egress allow-list, and content is never used to train shared models. Data is ingested into your tenant only.",
  },
  {
    key: "pipeline", n: "03", phase: "Extract into the graph", surface: "Pipelines",
    Icon: Workflow,
    generic:
      "Run the parse → chunk → normalize → embed → extract → resolve → graph-write → validate DAG. Language-agnostic concept normalization folds dialects, accents and transcription variants onto one canonical entity; sub-threshold duplicate merges are queued for human review, never applied silently. SHACL shapes enforce integrity and PII is redacted pre-flight.",
  },
  {
    key: "retrieval", n: "04", phase: "Configure governed retrieval", surface: "Retrieval",
    Icon: SearchIcon,
    generic:
      "Compose a hybrid profile fusing vector and graph evidence. A retrieval router picks the strategy per question — vector, graph or GraphRAG — and shows why. Access-control filters are applied to every candidate before fusion, so retrieval is permission-aware by construction rather than redacted after the fact.",
  },
  {
    key: "agents", n: "05", phase: "Deploy decision agents", surface: "Agents",
    Icon: Briefcase,
    generic:
      "Bind a system prompt, the published ontology and the retrieval profile to a tool-using agent. Activation passes an eval-regression gate, so no change that degrades the golden set ever reaches production. Bring your own LLM keys; every run is traced tool-by-tool, costed in dollars against a per-workspace budget cap, and citation-locked to its evidence. Proposed actions are approval-gated, dry-runnable, idempotent and reversible.",
  },
  {
    key: "governance", n: "06", phase: "Prove and audit", surface: "Governance",
    Icon: ShieldCheck,
    generic:
      "Lineage, audit, cost and confidence are recorded for every run, and a per-workspace daily budget cap is enforced at the gateway. Regression evaluations gate ontology, retrieval and agent changes before they reach production, producing the evidence trail a CFO and a regulator both sign off on.",
  },
];

/* ------------------------------------------------------------------ */
/* Interactive mission-control demos — a curated teaser, not a port.   */
/*                                                                      */
/* We maintain a set of fully built, scenario-driven "mission control"  */
/* simulators (one per solution below) that dramatize the platform end  */
/* to end on synthetic data. Porting their orchestration engine and ROI  */
/* models into this public marketing bundle would ship that proprietary  */
/* logic to every visitor's browser (any SPA bundle is inspectable via   */
/* view-source/devtools) — the opposite of a competitive advantage. So   */
/* this page only lists what each demo proves and gates real access      */
/* behind a corporate-email request, routed through the same contact     */
/* pipeline as every other lead here. Nothing about the simulators'       */
/* internals ships in this bundle.                                        */
const INTERACTIVE_DEMOS: { title: string; proves: string; industry: string }[] = [
  { title: "Catastrophe Intelligence", proves: "real-time multi-agent orchestration under a live event", industry: "Insurance" },
  { title: "Fraud Intelligence", proves: "relationship reasoning across a hidden network of claims", industry: "Insurance" },
  { title: "Claims Intelligence", proves: "autonomous, auditable end-to-end operations", industry: "Insurance" },
  { title: "Regulatory Intelligence", proves: "continuous, evidence-backed governance", industry: "Legal" },
  { title: "Capital Intelligence", proves: "financial optimization under constraints", industry: "Capital markets" },
  { title: "Distribution Intelligence", proves: "revenue acceleration across a partner network", industry: "Banking" },
  { title: "Customer Intelligence", proves: "contextual service across broker and agent personas", industry: "Insurance" },
  { title: "Knowledge Health", proves: "the platform auditing its own ontology and graph health", industry: "SecOps" },
];

interface UseCase {
  id: string;
  industry: string;
  title: string;
  Icon: LucideIcon;
  persona: string;
  pain: string;
  with: string;
  roi: { label: string; value: string; basis: string }[];
  stack: string[];
  /** Per-domain one-liners that slot into the canonical PLATFORM_PHASES. */
  blueprint: Record<BlueprintKey, string>;
}

const USE_CASES: UseCase[] = [
  {
    id: "biomed",
    industry: "Life sciences",
    title: "Biomedical research intelligence (ORB-Med)",
    Icon: FlaskConical,
    persona: "Translational research lead (rare-disease program, e.g. Retinitis Pigmentosa)",
    pain: "Assembling the gene → protein → pathway → mutation → trial evidence chain for a single disease means manually reconciling PubMed, ClinicalTrials.gov, UniProt and gene databases. It takes weeks, and contradictory findings stay buried under consensus.",
    with: "A governed knowledge graph links Gene –encodes→ Protein –participates_in→ Pathway –disrupted_by→ Mutation –causes→ Disease. Literature, Trial, Repurposing, Contradiction and Hypothesis agents answer cited questions across your corpus in minutes, surfacing disagreement rather than hiding it.",
    roi: [
      { label: "Evidence assembly", value: "weeks → hours",
        basis: "A scoped disease map (genes, pathways, active trials) is produced in a single session instead of a multi-week literature review" },
      { label: "Contradiction surfacing", value: "net-new signal",
        basis: "Graph traversal exposes conflicting edges (e.g. INHIBITS vs ACTIVATES on the same target) that vector-only RAG averages away" },
      { label: "Defensibility", value: "100% cited",
        basis: "Every hop carries its source PMID / NCT id and retrieval timestamp; no uncited model assertion enters a report" },
    ],
    stack: ["ORB-Med pack (MONDO / HPO / GO)", "EuropePMC + ClinicalTrials.gov v2 connectors", "GraphRAG profile", "5 research agents"],
    blueprint: {
      ontology: "Publish Disease, Gene, Mutation, Protein, Pathway, Drug, ClinicalTrial, Publication, Phenotype and Biomarker with directional relations (ENCODES, PARTICIPATES_IN, CAUSES, TARGETS, INHIBITS / ACTIVATES). Load MONDO, HPO and HGNC as taxonomies so synonyms (RHO = RP4 = OPN2) collapse onto one node.",
      sources: "Connect EuropePMC (JSON abstracts), ClinicalTrials.gov v2, UniProt and MyGene via the REST connector. Public corpora and any proprietary trial data are ingested into your tenant and governed identically.",
      pipeline: "parser.auto → chunk.text → embed.gateway → enrich.iri → enrich.taxonomy → extract.entities → graph.writer → validate.shacl → persist.document. Canonical IRIs and resolved synonyms are attached before graph-write.",
      retrieval: "An orb-med-graphrag profile fuses vector (weight 0.6) and graph (0.4) with reciprocal-rank fusion and an optional reranker; agents iterate graph queries for multi-hop pathway traversal.",
      agents: "Literature, Trial, Drug-Repurposing, Contradiction and Hypothesis agents, each bound to the ORB-Med ontology and the GraphRAG profile, citing PMIDs / NCT ids.",
      governance: "Lineage ties every claim to a PMID / NCT id; SHACL enforces invariants such as 'every Mutation links to exactly one Gene'; competency questions gate the ontology before publish.",
    },
  },
  {
    id: "claims",
    industry: "Insurance",
    title: "Claims triage & coverage Q&A",
    Icon: FileText,
    persona: "Claims adjuster (P&C insurer, 1k policies/day)",
    pain: "Adjusters open 40+ documents per claim (policy, endorsements, ACORD forms, prior emails) to answer one question: 'is this damage covered?'. Avg. 18 min/claim of doc-juggling.",
    with: "Ask the claims-triage agent in plain English. It cites the exact policy section, evaluates exclusions against the loss description, and shows the ACORD form lineage. Adjuster confirms in 3 min.",
    roi: [
      { label: "Time per claim", value: "−83%",
        basis: "18 min → 3 min ✕ 1,000 claims/day = 250 hrs/day reclaimed" },
      { label: "Annual labor savings", value: "$3.6M / 100 adjusters",
        basis: "250 hrs/day × 220 days × $65/hr loaded ≈ $3.6M" },
      { label: "Leakage reduction", value: "−2 to −4% on indemnity",
        basis: "Cited coverage decisions reduce overpayment; conservative ACE/Swiss Re benchmark" },
      { label: "Audit defense", value: "100% citations",
        basis: "Every answer carries lineage_id + ACL trail; no 'I think the LLM said …'" },
    ],
    stack: ["Ontoligent Insurance schema", "Hybrid retrieval", "Claims-triage agent", "Evals on 200 closed claims"],
    blueprint: {
      ontology: "Publish Ontoligent-authored Policy, Endorsement, Coverage, Exclusion, Claim, LossEvent and Party types, with HAS_COVERAGE, BARRED_BY and ATTACHED_TO relations. Add FIBO or ACORD alignment only when the workspace has documented rights to the selected source and version.",
      sources: "Connect the claims system, the policy admin system and the document store. Customer-supplied ACORD forms and mappings remain in the licensed workspace after rights attestation; Ontoligent does not bundle or redistribute them.",
      pipeline: "parser.auto (table-aware) → chunk.text → embed.gateway → extract.entities → graph.writer → validate.shacl → persist.document, so endorsements and exclusions become typed graph nodes.",
      retrieval: "A claims-hybrid profile combines vector recall over policy text with graph hops along Claim → Policy → Coverage; ACL keeps each adjuster within their book.",
      agents: "A claims-triage agent answers coverage questions, evaluates exclusions against the loss description, and returns the reserve with cited policy sections.",
      governance: "Each decision is replayable with its lineage id; the eval gate runs the agent against 200 closed claims before any ontology change ships.",
    },
  },
  {
    id: "kyc",
    industry: "Banking",
    title: "KYC / EDD enrichment",
    Icon: Banknote,
    persona: "Onboarding analyst (Tier-1 bank, 5k new entities/mo)",
    pain: "Analysts pivot between LexisNexis, internal CRM, transaction graphs, and adverse-media articles to assemble one EDD packet. 4–6 hrs per high-risk entity, error-prone.",
    with: "The KYC agent walks the entity graph (ownership chain, UBOs, transaction counterparties), pulls cited adverse media, and produces a draft packet with risk score + evidence in 12 min.",
    roi: [
      { label: "Time per EDD packet", value: "−95%",
        basis: "4 hrs → 12 min × 5,000 packets/mo = 19,000 hrs/mo saved" },
      { label: "Annual savings", value: "$11.4M",
        basis: "19,000 hrs/mo × 12 × $50/hr loaded ≈ $11.4M (large bank scale)" },
      { label: "Detection lift", value: "+22% true positives",
        basis: "Graph hop expansion catches indirect UBOs missed by string-match KYC" },
      { label: "Regulator ready", value: "Full lineage",
        basis: "Each evidence node has source URI + retrieval timestamp; FCA/OCC defensible" },
    ],
    stack: ["Graph + vector hybrid", "ABAC on PII", "Audit log", "Adverse-media connector"],
    blueprint: {
      ontology: "Publish Ontoligent-authored LegalEntity, NaturalPerson, BeneficialOwner, Account and Counterparty types with OWNS, CONTROLS and TRANSACTS_WITH relations. Optional FIBO alignment records the exact source licence and version.",
      sources: "Connect the CRM, the transaction store and an adverse-media feed. PII fields are tagged at ingestion so ABAC policies can gate them per role.",
      pipeline: "parser.auto → chunk.text → embed.gateway → pii.redact → extract.entities → graph.writer → validate.shacl, so ownership chains are reconstructed as graph edges.",
      retrieval: "A kyc-hybrid profile expands the ownership graph by N hops to reach indirect UBOs, then fuses cited adverse-media chunks; ABAC enforces need-to-know on PII.",
      agents: "An EDD agent assembles the packet — risk score, UBO chain, cited adverse media — and routes high-risk entities to human review.",
      governance: "Every evidence node carries source URI and timestamp; the audit log and cost ledger make the packet examiner-ready.",
    },
  },
  {
    id: "filings",
    industry: "Capital markets",
    title: "Filings & research synthesis",
    Icon: TrendingUp,
    persona: "Sell-side equity analyst",
    pain: "Reading 10-Ks, earnings transcripts, and broker notes to update a single industry model takes 6–10 hrs/week per analyst.",
    with: "Filings-qa agent answers grounded questions across the analyst's coverage universe with table-extracted financials and side-by-side YoY deltas. Same update in 45 min.",
    roi: [
      { label: "Hours reclaimed", value: "5–8 hrs / analyst / week",
        basis: "Conservative midpoint of 8 hrs → 1 hr deltas" },
      { label: "Coverage expansion", value: "+30–50% tickers",
        basis: "Same headcount can model more names; Goldman 'AI for analysts' 2024 internal lift" },
      { label: "Decision speed", value: "−4 days",
        basis: "Time to first published note after a 10-K shrinks from ~5 days to ~1 day" },
    ],
    stack: ["EDGAR connector", "Table-aware parser", "Filings-qa agent", "Hybrid retrieval"],
    blueprint: {
      ontology: "Publish Issuer, Filing, Segment, Metric and Period with REPORTS, BELONGS_TO and COMPARED_TO relations.",
      sources: "Connect EDGAR and your broker-note store via the REST and document connectors; coverage lists scope ingestion to the analyst's universe.",
      pipeline: "parser.auto (table structure on) → chunk.text → embed.gateway → extract.entities → graph.writer → persist.document, so financial tables become queryable Metric nodes.",
      retrieval: "A filings-hybrid profile retrieves narrative text by vector and joins extracted metrics by graph for year-over-year deltas.",
      agents: "A filings-qa agent answers grounded questions and emits side-by-side period comparisons with cited table provenance.",
      governance: "Lineage points to the exact filing and page; evals guard extraction accuracy on a labelled filings set.",
    },
  },
  {
    id: "pharma",
    industry: "Healthcare / Pharma",
    title: "Clinical evidence synthesis",
    Icon: Stethoscope,
    persona: "Medical Science Liaison (MSL) supporting a launch",
    pain: "An MSL receives a question from an HCP and digs through PubMed, internal trial data, and prior MSL responses. Median turnaround: 2 business days.",
    with: "With a customer-licensed SNOMED CT edition configured, the evidence agent retrieves aligned clinical concepts across PubMed + internal CSRs, produces a cited summary, and flags off-label risk. Turnaround: same day, 30 min.",
    roi: [
      { label: "Response SLA", value: "2 days → 30 min",
        basis: "Reduces MSL response backlog and HCP dissatisfaction" },
      { label: "MSL capacity", value: "+40% inquiries handled",
        basis: "Same team handles more without sacrificing rigor" },
      { label: "Compliance", value: "0 off-label slips",
        basis: "Citation-locked answers + ABAC on indication/region prevents off-label drift" },
    ],
    stack: ["Customer-licensed terminology", "PubMed connector", "PII redactor", "Evals on Q/A from MSL archive"],
    blueprint: {
      ontology: "Publish Ontoligent-authored ClinicalConcept, Indication, Study, Endpoint and Evidence types with SUPPORTS, CONTRAINDICATES and STUDIED_IN relations. SNOMED CT alignment requires a customer-supplied licensed edition and recorded rights evidence.",
      sources: "Connect PubMed and your internal CSR / prior-response store; indication and region tags drive ABAC at retrieval time.",
      pipeline: "parser.auto → chunk.text → embed.gateway → pii.redact → extract.entities → graph.writer → validate.shacl, optionally aligning concepts to customer-licensed SNOMED CT codes.",
      retrieval: "An evidence-hybrid profile retrieves concepts aligned to the customer's licensed SNOMED CT edition across PubMed and internal CSRs, ACL-scoped by indication and region.",
      agents: "An evidence agent produces a cited summary and flags off-label risk before an MSL sends it.",
      governance: "Citation-locked answers plus ABAC on indication / region prevent off-label drift; evals run against the MSL Q/A archive.",
    },
  },
  {
    id: "secops",
    industry: "SecOps",
    title: "Threat hunt & incident enrichment",
    Icon: ShieldCheck,
    persona: "Tier-2 SOC analyst",
    pain: "Triaging an alert means pivoting Splunk → CrowdStrike → MISP → wiki run-books. 35–60 min per alert; alert fatigue causes ~25% to be closed without full enrichment.",
    with: "The hunt agent grounds in MITRE ATT&CK ontology, walks the asset+identity graph, pulls relevant past incidents and run-books, and proposes a containment playbook with confidence + cost.",
    roi: [
      { label: "MTTR per alert", value: "−65%",
        basis: "45 min → 16 min median triage + enrichment" },
      { label: "Alerts fully enriched", value: "75% → 99%",
        basis: "Eliminates 'closed without analysis' due to fatigue" },
      { label: "Breach cost avoidance", value: "$1.7M / breach",
        basis: "IBM 2024 — every 100-day faster containment ≈ $1.7M lower breach cost" },
    ],
    stack: ["MITRE ATT&CK pack", "Asset / identity graph", "MCP connectors (SIEM, EDR)"],
    blueprint: {
      ontology: "Publish Asset, Identity, Alert, Technique, Incident and Runbook with TARGETS, USES_TECHNIQUE and MITIGATED_BY relations from the MITRE ATT&CK pack.",
      sources: "Connect SIEM and EDR via MCP connectors and the run-book store; tool access is scoped by workspace credentials.",
      pipeline: "Ingest alerts and incidents → extract.entities → graph.writer → validate.shacl, building the asset / identity graph that enrichment traverses.",
      retrieval: "A hunt-hybrid profile joins the alert's asset graph to prior incidents and run-books, ACL-scoped to the analyst's estate.",
      agents: "A hunt agent proposes a containment playbook with confidence and projected cost, citing the techniques and prior incidents it relied on.",
      governance: "Every enrichment is traced and costed; confidence scores and lineage support post-incident review.",
    },
  },
  {
    id: "legal",
    industry: "Legal",
    title: "Contract review & playbook",
    Icon: Gavel,
    persona: "In-house counsel reviewing 200 NDAs/quarter",
    pain: "Linear NDA review with track-changes against a Word playbook: 35 min/NDA. Junior associates miss 8–12% of deviations.",
    with: "The contract agent extracts clauses against the playbook ontology, flags deviations with severity, and proposes redlines. Reviewer signs off in 6 min.",
    roi: [
      { label: "Time per NDA", value: "−83%",
        basis: "35 min → 6 min × 800/yr = ~390 hrs reclaimed" },
      { label: "Deviation recall", value: "92% → 99%",
        basis: "Ontology-driven clause matching beats keyword search" },
      { label: "Outside counsel spend", value: "−$300k/yr",
        basis: "Mid-market in-house team: defer 60 hrs/mo of overflow at $400/hr" },
    ],
    stack: ["Legal pack (clause taxonomy)", "Diff-aware retrieval", "Eval gate on 50 historical redlines"],
    blueprint: {
      ontology: "Publish Contract, Clause, Obligation, Party and PlaybookRule with CONTAINS, DEVIATES_FROM and GOVERNS relations; the clause taxonomy ships in the Legal pack.",
      sources: "Connect the contract repository and the playbook document; everything stays inside the workspace.",
      pipeline: "parser.auto → chunk.text (clause-aware) → embed.gateway → extract.entities → graph.writer → validate.shacl, mapping each clause to its playbook rule.",
      retrieval: "A diff-aware profile aligns extracted clauses to playbook rules and ranks deviations by severity.",
      agents: "A contract agent flags deviations and proposes redlines with the governing playbook rule cited.",
      governance: "An eval gate replays 50 historical redlines before any taxonomy change; every flag is lineage-linked.",
    },
  },
];

export function UseCases() {
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(USE_CASES[0]?.id ?? null);
  const [demoAccessOpen, setDemoAccessOpen] = useState(false);
  const [demoAccessTopic, setDemoAccessTopic] = useState<string | null>(null);

  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    // Without a query, the page is driven by the graph: only the selected
    // domain's blueprint is shown; the others stay hidden until picked.
    if (!n) {
      const open = USE_CASES.find((u) => u.id === openId);
      return open ? [open] : USE_CASES.slice(0, 1);
    }
    const filtered = USE_CASES.filter((u) =>
      (u.title + " " + u.industry + " " + u.persona + " " + u.pain + " " + u.with)
        .toLowerCase().includes(n)
    );
    // Keep the open card visible even when it falls outside the current query.
    if (openId && !filtered.some((u) => u.id === openId)) {
      const open = USE_CASES.find((u) => u.id === openId);
      if (open) return [open, ...filtered];
    }
    return filtered;
  }, [q, openId]);

  // Open a domain's blueprint from the graph and bring it into view.
  const selectCase = (id: string) => {
    setOpenId(id);
    setQ("");
    setTimeout(() => {
      document.getElementById(`uc-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  // Portfolio ROI across every mapped domain (the full book, not just the
  // visible card) — the enterprise-scale headline, broken out per domain as a
  // bar chart below.
  const portfolio = useMemo(() => {
    const rows = USE_CASES.map((u) => {
      const m = u.roi.map((r) => r.value).join(" ").match(/\$([\d.]+)\s*M/i);
      return {
        id: u.id, industry: u.industry,
        value: m ? parseFloat(m[1]) : 0, color: industryColor(u.industry),
      };
    }).sort((a, b) => b.value - a.value);
    const total = rows.reduce((s, r) => s + r.value, 0);
    const max = Math.max(...rows.map((r) => r.value), 1);
    return { rows, total, max };
  }, []);

  return (
    <>
      {/* HERO — a concise value proposition beside a product-interface mock,
          replacing the previous wall of banners. */}
      <section className="mb-5 grid items-center gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/5 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-accent">
            <Lock size={12} /> Your data · your tenant · your keys
          </div>
          <h1 className="text-3xl font-bold leading-[1.08] tracking-tight md:text-[2.6rem]">
            Decision systems for regulated work, on your own knowledge graph.
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted">
            Pick an industry from the graph and watch the same six governed phases assemble
            its implementation blueprint — with conservative, analyst-benchmarked ROI. Every
            scenario runs on an isolated tenant under your own model keys.
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <a href="#domains" className="btn-primary">Explore the domain graph <ChevronRight size={14} /></a>
            <a href="#portfolio" className="btn">See the ROI</a>
          </div>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-muted">
            <span className="inline-flex items-center gap-1.5"><Database size={12} className="text-accent" /> Schema-per-tenant isolation</span>
            <span className="inline-flex items-center gap-1.5"><KeyRound size={12} className="text-accent" /> Bring your own keys</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-accent" /> Governed by construction</span>
          </div>
        </div>
        <HeroMock />
      </section>

      {/* Commercial engagement model — the "software-vendor play". */}
      <VendorPlay useCase={USE_CASES.find((u) => u.id === openId) ?? USE_CASES[0]} />

      {/* Interactive knowledge-graph explorer — replaces flat filter tabs.
         Domains orbit the ontology kernel; selecting one lights the shared
         six-phase path and opens its blueprint below. */}
      <section id="domains" className="scroll-mt-20">
        <UseCaseGraph
          useCases={USE_CASES}
          phases={PLATFORM_PHASES}
          query={q}
          onQuery={setQ}
          selectedId={openId}
          onSelect={selectCase}
          industryColor={industryColor}
        />
      </section>

      {/* Portfolio ROI — the headline enterprise metric, rendered as a chart. */}
      <section id="portfolio" className="card mb-4 mt-4 scroll-mt-20">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-stretch">
          <div className="lg:w-64 lg:shrink-0 lg:border-r lg:border-border lg:pr-6">
            <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted">
              <TrendingUp size={14} className="text-accent" /> Portfolio annual savings
            </div>
            <div className="stat-mono text-4xl">
              ${portfolio.total.toFixed(1)}M<span className="text-base font-normal text-muted"> / yr</span>
            </div>
            <p className="mt-2 text-[11px] leading-snug text-muted">
              Sum of the first $-denominated line across all {portfolio.rows.length} mapped
              domains. Conservative analyst-benchmarked midpoints — cite each card&apos;s
              &quot;basis&quot; line when modelling internally.
            </p>
          </div>
          <div className="flex-1 space-y-2">
            {portfolio.rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => selectCase(r.id)}
                className="group flex w-full items-center gap-3 text-left"
                title={`Open ${r.industry} blueprint`}
              >
                <span className="w-32 shrink-0 truncate text-[11px] font-medium">{r.industry}</span>
                <span className="relative h-5 flex-1 overflow-hidden rounded-md bg-border/40">
                  <span
                    className="absolute inset-y-0 left-0 rounded-md transition-all duration-300 group-hover:brightness-110"
                    style={{ width: `${Math.max(6, (r.value / portfolio.max) * 100)}%`, background: r.value ? r.color : "hsl(var(--border))" }}
                  />
                </span>
                <span className="w-14 shrink-0 text-right font-mono text-xs text-accent">
                  {r.value ? `$${r.value.toFixed(1)}M` : "\u2014"}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive mission-control demos — teaser grid, access-gated (see
          INTERACTIVE_DEMOS comment above for why we don't ship these live). */}
      <section id="interactive-demos" className="card mb-4 scroll-mt-20">
        <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted">
          <Rocket size={14} className="text-accent" /> Interactive mission-control demos
        </div>
        <p className="mb-4 max-w-2xl text-sm leading-relaxed text-muted">
          Beyond this page's blueprints, we run scenario-driven, mission-control-style
          simulations of each solution end to end on synthetic data. They're reserved for
          qualified conversations rather than public embeds — request access with a
          corporate email and we'll walk you through the one closest to your use case.
        </p>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {INTERACTIVE_DEMOS.map((d) => (
            <button
              key={d.title}
              type="button"
              onClick={() => { setDemoAccessTopic(d.title); setDemoAccessOpen(true); }}
              className="group flex flex-col items-start gap-1 rounded-lg border border-border p-3 text-left transition hover:border-accent/50 hover:bg-accent/5"
            >
              <span className="text-[10px] font-medium uppercase tracking-wide text-accent">{d.industry}</span>
              <span className="text-sm font-semibold">{d.title}</span>
              <span className="text-xs leading-snug text-muted">Proves {d.proves}.</span>
              <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-accent opacity-0 transition-opacity group-hover:opacity-100">
                Request access <ChevronRight size={12} />
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Use case list — one open at a time */}
      <div className="space-y-2">
        {list.map((u) => {
          const isOpen = openId === u.id;
          const headlineRoi = u.roi[0];
          return (
            <article key={u.id} id={`uc-${u.id}`} className="card card-interactive scroll-mt-20 p-0">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenId(isOpen ? null : u.id)}
                className="flex w-full items-center gap-4 px-4 py-3 text-left"
              >
                <ChevronRight
                  size={16}
                  className={`shrink-0 text-muted transition-transform ${isOpen ? "rotate-90 text-accent" : ""}`}
                />
                <u.Icon size={20} className="shrink-0 text-accent" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="pill">{u.industry}</span>
                    <h3 className="text-base font-semibold leading-tight">{u.title}</h3>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted">{u.persona}</p>
                </div>
                {headlineRoi && (
                  <div className="hidden shrink-0 text-right md:block">
                    <div className="font-mono text-sm text-accent">{headlineRoi.value}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted">{headlineRoi.label}</div>
                  </div>
                )}
              </button>

              {isOpen && (
                <div className="border-t border-border/60 px-4 py-4">
                  <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="rounded-md border border-bad/30 bg-bad/5 p-2">
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-bad">Today</div>
                      <p className="text-xs leading-relaxed">{u.pain}</p>
                    </div>
                    <div className="rounded-md border border-good/30 bg-good/5 p-2">
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-good">With Ontoligent</div>
                      <p className="text-xs leading-relaxed">{u.with}</p>
                    </div>
                  </div>

                  {/* Implementation blueprint — DAG render of the six phases. */}
                  <ImplementationBlueprint blueprint={u.blueprint} />

                  <div className="mb-3 mt-4">
                    <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted">
                      <TrendingUp size={12} /> ROI
                    </div>
                    <RoiTiles roi={u.roi} />
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {u.stack.map((s) => (
                      <span key={s} className="pill text-[10px]">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </article>
          );
        })}
        {list.length === 0 && (
          <div className="card text-center text-muted">No use case matches the current filter.</div>
        )}
      </div>

      
      {/* Why-this-vs-alternatives strip */}
      <div className="card mb-4 mt-6 border-accent/40">
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <Lightbulb size={16} className="text-accent" /> Why Ontoligent vs. the alternatives?
        </h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <CompareCard title="vs. SaaS chatbot (ChatGPT Enterprise / Copilot)"
            wins={[
              "Permission-aware retrieval (ACL inside the index, not after)",
              "Citations with lineage_id, not just document titles",
              "Ontology-grounded answers, not generic web text",
              "Cost & audit per workspace / pipeline / agent run",
            ]} />
          <CompareCard title="vs. DIY LangChain / LlamaIndex"
            wins={[
              "No glue code: ontology, ACL, lineage, evals are first-class",
              "Multi-tenant by construction (django-tenants, per-org schemas)",
              "Regression-eval gates ship; you don't reinvent them",
              "Governed domain starters; licensed sources stay customer-supplied",
            ]} />
          <CompareCard title="vs. Enterprise search (Glean, Coveo)"
            wins={[
              "Agents that act, not just retrieve",
              "Graph + vector + BM25 hybrid (not vector-only)",
              "BYO LLM / BYO storage / on-prem privacy modes",
              "Open ontology you can govern, version, and rollback",
            ]} />
        </div>
      </div>

      {/* Closing argument */}
      <div className="card mt-6 border-accent/40">
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <Briefcase size={16} className="text-accent" /> The deliverable, not just the answer
        </h2>
        <p className="text-sm leading-relaxed">
          The simplest mental model: <strong>generic RAG saves minutes; Ontoligent
          unlocks decisions you can defend.</strong> The numbers above are mostly
          labor-cost reductions — real, but the larger lever in regulated work is the
          output you are allowed to ship: cited, permissioned, lineaged, versioned. A
          claims decision, an EDD packet, an MSL response, an analyst note, a disease
          evidence map — each becomes auditable evidence rather than chatbot output,
          produced on your own tenant under your own keys.
        </p>
      </div>
      <ContactModal
        open={demoAccessOpen}
        onClose={() => setDemoAccessOpen(false)}
        kind="access"
        title={demoAccessTopic ? `Request access — ${demoAccessTopic}` : "Request demo access"}
        subtitle="Interactive mission-control simulations are reserved for qualified conversations. Share a corporate email and we'll set up a walkthrough on the scenario closest to your use case."
        cta="Request access"
      />
    </>
  );
}

function ImplementationBlueprint({ blueprint }: { blueprint: Record<BlueprintKey, string> }) {
  const [pinned, setPinned] = useState<BlueprintKey | null>(null);
  const [hover, setHover] = useState<BlueprintKey | null>(null);
  const activeKey = hover ?? pinned;
  const active = PLATFORM_PHASES.find((p) => p.key === activeKey) ?? null;
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted">
        <span className="flex items-center gap-1"><ListChecks size={12} /> Implementation blueprint — a governed DAG, six phases on your tenant</span>
        <span className="text-[10px] font-normal normal-case text-muted/70">· hover or click a phase for detail</span>
      </div>

      {/* Phase rail. A responsive auto-fit grid that always fills the full
          section width: on wide screens the six cards stretch edge-to-edge; as
          the viewport narrows they reflow into balanced rows that stay centered
          and full-width — never leaving the dead space a fixed-width scroll row
          would. This is the intelligent fallback for small containers. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(148px,1fr))] gap-2">
        {PLATFORM_PHASES.map((p, i) => {
          const isActive = activeKey === p.key;
          const isPinned = pinned === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setPinned(isPinned ? null : p.key)}
              onMouseEnter={() => setHover(p.key)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(p.key)}
              onBlur={() => setHover(null)}
              aria-pressed={isPinned}
              className={`group relative flex flex-col overflow-hidden rounded-xl border p-3 text-left transition-all duration-200 ${
                isActive
                  ? "border-accent/70 bg-accent/[0.07] shadow-[0_10px_28px_-16px_rgba(0,0,0,0.65)] ring-1 ring-accent/25"
                  : "border-border bg-bg/40 hover:border-accent/40 hover:bg-bg/70"
              }`}
            >
              {/* directional flow rail across the top of the card */}
              <span className={`pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-accent/70 via-accent/30 to-transparent transition-opacity ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-60"}`} />
              <div className="mb-2 flex items-center justify-between">
                <span className={`flex h-7 w-7 items-center justify-center rounded-lg border font-mono text-[11px] transition-colors ${isActive ? "border-accent/60 bg-accent/15 text-accent" : "border-border bg-bg text-muted group-hover:text-accent"}`}>{p.n}</span>
                <p.Icon size={15} className={isActive ? "text-accent" : "text-muted transition-colors group-hover:text-accent"} />
              </div>
              <div className="text-[12px] font-semibold leading-tight text-text">{p.phase}</div>
              <div className="mt-0.5 text-[9px] uppercase tracking-wider text-muted">{p.surface}</div>
              {/* DAG flow cue to the next phase — decorative, sits in the gap on
                  wide single-row layouts and is hidden when the rail wraps. */}
              {i < PLATFORM_PHASES.length - 1 && (
                <ChevronRight
                  size={14}
                  aria-hidden
                  className="pointer-events-none absolute -right-[11px] top-1/2 hidden -translate-y-1/2 text-muted/40 xl:block"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Detail inspector. One elevated panel below the rail driven by hover
          (preview) or click (pinned). Reads like a product inspector rather
          than a cramped per-card expander, and never clips against neighbours. */}
      <div className={`grid transition-all duration-300 ease-out ${active ? "mt-2 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          {active && (
            <div className="relative rounded-xl border border-accent/40 bg-panel/80 p-4 shadow-[0_20px_48px_-24px_rgba(0,0,0,0.7)] backdrop-blur-sm">
              <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />
              <div className="mb-2 flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-accent/50 bg-accent/10 text-accent">
                  <active.Icon size={18} />
                </span>
                <div className="min-w-0 pt-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[11px] text-accent">{active.n}</span>
                    <h4 className="text-sm font-semibold text-text">{active.phase}</h4>
                    <span className="pill text-[9px]">{active.surface}</span>
                  </div>
                </div>
                {pinned && (
                  <button
                    type="button"
                    onClick={() => setPinned(null)}
                    aria-label="Unpin detail"
                    className="btn-ghost -mr-1 -mt-1 ml-auto shrink-0 text-muted"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <p className="text-[12px] leading-relaxed text-text/90">{blueprint[active.key]}</p>
              <p className="mt-2 border-t border-border/60 pt-2 text-[11px] leading-relaxed text-muted">{active.generic}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Per-card ROI as compact metric tiles with a relative bar gauge — a more
   enterprise-grade read than a plain list. */
function RoiTiles({ roi }: { roi: { label: string; value: string; basis: string }[] }) {
  const nums = roi.map((r) => {
    const m = r.value.match(/([\d.]+)/);
    return m ? parseFloat(m[1]) : 0;
  });
  const max = Math.max(...nums, 1);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {roi.map((r, i) => (
        <div key={i} className="stat-tile">
          <div className="flex items-baseline justify-between gap-2">
            <span className="stat-mono text-lg text-accent">{r.value}</span>
            <span className="label-micro text-right">{r.label}</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border/60">
            <div
              className="h-full rounded-full bg-gradient-to-r from-accent to-accent/40"
              style={{ width: `${Math.max(8, (nums[i] / max) * 100)}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-muted">{r.basis}</p>
        </div>
      ))}
    </div>
  );
}

/* A framed product-interface mock for the hero — an application-agnostic
   window frame (no OS-specific traffic-light dots) showing a realistic
   snapshot of the studio: a left nav rail, a tab bar, the domain graph and a
   blueprint/ROI panel. */
function HeroMock() {
  return <KnowledgeGraphIllustration />;
}

/* Per-industry framing for the engagement section. Each quote follows the same
   honest shape: we do NOT claim to do the customer's hard job; instead, inside
  a verified customer-hosted deployment, we map their private corpus
   against public knowledge and surface cited, missing links fast. The wording
   deliberately avoids "grant us your data" — nothing is handed over; the
   platform runs where the data already lives. */
interface VendorQuote { claim: string; outcome: string; caption: string; }
const VENDOR_QUOTES: Record<string, VendorQuote> = {
  "Life sciences": {
    claim: "We don’t claim to cure your target disease.",
    outcome: "maps your private corpus against the world’s public research and surfaces the missing links — each one cited",
    caption: "The position taken into a pharmaceutical company or research hospital: not a biotech placing a risky therapeutic bet, but the governed data infrastructure that turns their private corpus into defensible, cited intelligence.",
  },
  "Insurance": {
    claim: "We don’t claim to settle your claims for you.",
    outcome: "maps your policy and loss history against your governed insurance schema and, when licensed, customer-supplied ACORD sources — each result cited",
    caption: "Positioned to a carrier: not a third-party administrator touching your decisions, but the governed layer that makes every coverage call cited, permissioned and audit-ready.",
  },
  "Banking": {
    claim: "We don’t claim to make your risk decisions.",
    outcome: "reconciles your counterparty data against public registries and adverse media, surfacing hidden ownership links — each one cited",
    caption: "Positioned to a bank: not an outsourced KYC vendor exporting your data, but the governed graph that assembles examiner-ready evidence inside your own boundary.",
  },
  "Capital markets": {
    claim: "We don’t claim to call the market.",
    outcome: "maps your coverage universe against public filings and surfaces the deltas that move a model — each one cited",
    caption: "Positioned to a desk: not a research outsourcer, but the governed layer that grounds every note in cited, traceable filings.",
  },
  "Healthcare / Pharma": {
    claim: "We don’t claim to practice medicine.",
    outcome: "aligns your internal evidence with public literature and surfaces the cited answer, off-label risk flagged",
    caption: "Positioned to a medical affairs team: not a clinical decision-maker, but the governed evidence layer that keeps every response cited and on-label.",
  },
  "SecOps": {
    claim: "We don’t claim to run your SOC.",
    outcome: "maps alerts against MITRE ATT&CK and your incident history and surfaces the containment path — each step cited",
    caption: "Positioned to a security team: not a managed-detection vendor pulling your telemetry out, but the governed graph that enriches alerts inside your estate.",
  },
  "Legal": {
    claim: "We don’t claim to give legal advice.",
    outcome: "maps your contracts against your playbook and public filings and surfaces every deviation — each one cited",
    caption: "Positioned to in-house counsel: not outside counsel, but the governed layer that makes every redline traceable to its governing rule.",
  },
};
const VENDOR_QUOTE_FALLBACK: VendorQuote = {
  claim: "We don’t claim to do your hardest job for you.",
  outcome: "maps your private corpus against trusted public knowledge and surfaces the missing links — each one cited",
  caption: "The platform is the governed wrapper around your data; the proprietary advantage stays yours.",
};

function VendorPlay({ useCase }: { useCase: UseCase }) {
  const quote = VENDOR_QUOTES[useCase.industry] ?? VENDOR_QUOTE_FALLBACK;
  return (
    <section className="card mb-4 overflow-hidden border-accent/40">
      <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted">
        <Briefcase size={14} className="text-accent" /> How we’d start working with your team
      </div>

      <figure className="relative rounded-md border border-accent/30 bg-accent/5 p-4 md:p-5">
        <Quote size={20} className="mb-2 text-accent" aria-hidden />
        <blockquote className="text-base leading-relaxed md:text-lg">
          “{quote.claim} Under a verified customer-hosted deployment, content and keys can
          remain inside your approved environment — our Enterprise Intelligence Platform
          {" "}{quote.outcome}, in 48 hours.”
        </blockquote>
        <figcaption className="mt-3 text-xs text-muted">
          {quote.caption}
        </figcaption>
      </figure>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <VendorStep
          Icon={Database}
          n="01"
          title="Your data stays in your tenant"
          body="Your sources are read in place, inside your isolated tenant, under your egress allow-list. Nothing is copied out of your boundary and nothing is ever used to train shared models — you keep custody and your own keys throughout."
        />
        <VendorStep
          Icon={Network}
          n="02"
          title="We map it against public knowledge"
          body="Your corpus is reconciled against trusted open sources — registries, literature, filings or threat intel — onto one versioned ontology, so private and public facts share the same typed graph without your data leaving its home."
        />
        <VendorStep
          Icon={Clock}
          n="03"
          title="Cited missing links in 48 hours"
          body="Graph traversal and the reasoning agents surface under-explored connections and conflicting evidence, each link cited to its source — a scoped, governed proof of value, not a multi-quarter program."
        />
      </div>

      <p className="mt-4 text-xs leading-relaxed text-muted">
        The same engagement generalizes across domains: a bank’s counterparty data mapped
        against public registries and adverse media; an insurer’s claims history mapped
        against industry loss data; a law firm’s matter archive mapped against public
        filings — always in place, inside the customer’s tenant. The platform is the
        governed wrapper; the proprietary advantage stays the customer’s.
      </p>
    </section>
  );
}

function VendorStep({ Icon, n, title, body }: { Icon: LucideIcon; n: string; title: string; body: string }) {
  return (
    <div className="rounded-md border border-border bg-bg/50 p-3">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-accent/40 font-mono text-[11px] text-accent">
          {n}
        </span>
        <Icon size={14} className="text-accent" />
        <span className="text-xs font-semibold">{title}</span>
      </div>
      <p className="text-xs leading-relaxed text-muted">{body}</p>
    </div>
  );
}

function CompareCard({ title, wins }: { title: string; wins: string[] }) {
  return (
    <div className="rounded-md border border-border bg-bg/50 p-3">
      <div className="mb-2 text-xs font-semibold">{title}</div>
      <ul className="space-y-1 text-xs">
        {wins.map((w, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{w}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
